import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X, Heart, ShoppingCart, Plus, Minus, Star, Truck, Shield, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';

// Helper function to strip HTML tags and add proper spacing
const stripHtml = (html) => {
  if (!html) return '';
  return html
    .replace(/<\/li>/gi, '. ')  // Add period and space after list items
    .replace(/<\/p>/gi, ' ')    // Add space after paragraphs
    .replace(/<br\s*\/?>/gi, ' ') // Replace line breaks with spaces
    .replace(/<[^>]*>/g, '')    // Remove remaining HTML tags
    .replace(/&nbsp;/g, ' ')    // Replace HTML entities
    .replace(/\s+/g, ' ')       // Normalize multiple spaces to single space
    .trim();
};

const QuickViewModal = ({ product, isOpen, onClose }) => {
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  const { addToWishlist, isInWishlist, removeFromWishlist } = useWishlist();

  if (!isOpen || !product) return null;

  const displayPrice = product.sale_price || product.salePrice || product.price;
  const originalPrice = product.sale_price || product.salePrice ? product.price : null;
  const discount = originalPrice ? Math.round((1 - displayPrice / originalPrice) * 100) : 0;
  const inWishlist = isInWishlist(product._id || product.id);
  const isOutOfStock = product.in_stock === false || product.inStock === false;

  const handleAddToCart = () => {
    if (isOutOfStock) return;
    const productId = product._id || product.id;
    addToCart(productId, quantity, product.name, product.images?.[0] || product.image);
    onClose();
  };

  const handleWishlist = () => {
    if (inWishlist) {
      removeFromWishlist(product._id || product.id);
    } else {
      addToWishlist({
        id: product._id || product.id,
        name: product.name,
        price: product.price,
        salePrice: product.sale_price || product.salePrice,
        image: product.images?.[0] || product.image,
        slug: product.slug
      });
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="relative bg-[#141414] rounded-2xl shadow-2xl shadow-[#D4AF37]/20 max-w-4xl w-full max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200 border border-[#D4AF37]/30"
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 bg-[#0A0A0A]/90 hover:bg-[#D4AF37] rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 border border-[#D4AF37]/30"
        >
          <X className="h-5 w-5 text-white hover:text-[#0A0A0A]" />
        </button>

        <div className="grid md:grid-cols-2">
          {/* Image Section */}
          <div className="relative bg-[#1A1A1A] p-6 md:p-8">
            {/* Badges */}
            <div className="absolute top-4 left-4 flex flex-col gap-2 z-10">
              {product.is_new && (
                <span className="px-3 py-1 bg-[#D4AF37] text-[#0A0A0A] text-xs font-bold rounded-full">NEW</span>
              )}
              {discount > 0 && (
                <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full">-{discount}%</span>
              )}
              {isOutOfStock && (
                <span className="px-3 py-1 bg-[#333] text-[#888] text-xs font-bold rounded-full">OUT OF STOCK</span>
              )}
            </div>

            {/* Wishlist button */}
            <button
              onClick={handleWishlist}
              className={`absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all border ${
                inWishlist 
                  ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]' 
                  : 'bg-[#0A0A0A]/80 text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] border-[#D4AF37]/30'
              }`}
            >
              <Heart className={`h-5 w-5 ${inWishlist ? 'fill-current' : ''}`} />
            </button>

            <img 
              src={product.images?.[0] || product.image || 'https://via.placeholder.com/400'} 
              alt={product.name}
              className="w-full h-64 md:h-80 object-contain"
            />
          </div>

          {/* Content Section */}
          <div className="p-6 md:p-8 flex flex-col bg-[#141414]">
            {/* Category */}
            <p className="text-[#D4AF37] font-medium text-sm mb-2 font-body">{product.category}</p>
            
            {/* Title */}
            <h2 className="text-xl md:text-2xl font-bold text-white mb-3 font-heading line-clamp-2">
              {product.name}
            </h2>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex">
                {[1,2,3,4,5].map(star => (
                  <Star 
                    key={star}
                    className={`h-4 w-4 ${star <= (product.rating || 4) ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#333]'}`}
                  />
                ))}
              </div>
              <span className="text-sm text-[#888888] font-body">({product.review_count || 0} reviews)</span>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-4">
              <span className="text-3xl font-bold text-[#D4AF37] font-heading">${displayPrice?.toFixed(2)}</span>
              {originalPrice && (
                <span className="text-lg text-[#888888] line-through font-body">${originalPrice.toFixed(2)}</span>
              )}
            </div>

            {/* Description - Strip HTML tags */}
            <p className="text-[#888888] font-body text-sm mb-6 line-clamp-3">
              {stripHtml(product.description)}
            </p>

            {/* Quick specs */}
            {(product.size || product.color || product.material) && (
              <div className="flex flex-wrap gap-2 mb-6">
                {product.size && (
                  <span className="px-3 py-1 bg-[#0A0A0A] text-[#D4AF37] text-xs rounded-full font-body border border-[#D4AF37]/30">
                    Size: {product.size}
                  </span>
                )}
                {product.color && (
                  <span className="px-3 py-1 bg-[#0A0A0A] text-[#D4AF37] text-xs rounded-full font-body border border-[#D4AF37]/30">
                    Color: {product.color}
                  </span>
                )}
                {product.material && (
                  <span className="px-3 py-1 bg-[#0A0A0A] text-[#D4AF37] text-xs rounded-full font-body border border-[#D4AF37]/30">
                    {product.material}
                  </span>
                )}
              </div>
            )}

            {/* Quantity */}
            <div className="flex items-center gap-4 mb-6">
              <span className="text-sm font-medium text-white font-body">Quantity:</span>
              <div className="flex items-center border border-[#D4AF37]/30 rounded-lg bg-[#0A0A0A]">
                <button 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 flex items-center justify-center hover:bg-[#1A1A1A] transition-colors text-white"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-12 text-center font-medium text-white font-body">{quantity}</span>
                <button 
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 flex items-center justify-center hover:bg-[#1A1A1A] transition-colors text-white"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Add to Cart */}
            {!isOutOfStock ? (
              <Button 
                onClick={handleAddToCart}
                className="w-full bg-[#D4AF37] hover:bg-[#B8960C] text-[#0A0A0A] py-6 text-lg font-bold font-body rounded-xl mb-4"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                Add to Cart - ${(displayPrice * quantity).toFixed(2)}
              </Button>
            ) : (
              <Button 
                disabled
                className="w-full bg-[#333] text-[#888] py-6 text-lg font-bold font-body rounded-xl mb-4 cursor-not-allowed"
              >
                Out of Stock
              </Button>
            )}

            {/* View Full Details */}
            <Link to={`/shop/product/${product.slug}`} onClick={onClose}>
              <Button variant="outline" className="w-full py-4 font-body font-bold rounded-xl border-2 border-[#D4AF37] text-[#0A0A0A] bg-[#D4AF37]/20 hover:bg-[#D4AF37] hover:text-[#0A0A0A]">
                View Full Details
              </Button>
            </Link>

            {/* Trust badges */}
            <div className="flex justify-center gap-6 mt-6 pt-6 border-t border-[#D4AF37]/20">
              <div className="flex items-center gap-2 text-[#888888]">
                <Truck className="h-4 w-4 text-[#D4AF37]" />
                <span className="text-xs font-body">Free Shipping</span>
              </div>
              <div className="flex items-center gap-2 text-[#888888]">
                <Shield className="h-4 w-4 text-[#D4AF37]" />
                <span className="text-xs font-body">Secure</span>
              </div>
              <div className="flex items-center gap-2 text-[#888888]">
                <RotateCcw className="h-4 w-4 text-[#D4AF37]" />
                <span className="text-xs font-body">30-Day Returns</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document body level
  return createPortal(modalContent, document.body);
};

export default QuickViewModal;
